import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ChatPanel from '../tachograph/ChatPanel';
import { ANDRE_BRIDGE_VERSION, acceptsAndreReady, allowedAndreHost, cleanAndreContext } from '../../lib/andreBridge';
import './andre-workspace.css';

const AndreContext = createContext(null);
const PREVIEW = import.meta.env.DEV && import.meta.env.VITE_DESIGN_PREVIEW === 'true';

async function answerQuestion(question, context) {
  if (PREVIEW) {
    const { sampleAndreReply } = await import('../../../preview/andreDemo.mjs');
    return sampleAndreReply(question, context);
  }
  // Existing standalone service stays intact until the shared GPT integration
  // is implemented. Context is shown in the UI; it is not silently added to
  // this legacy endpoint's request or treated as an authenticated data source.
  const token = localStorage.getItem('tachoparser_token');
  const response = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ question }),
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body?.detail || `HTTP ${response.status}`);
  return body;
}

/* A request id that only has to be unique within this page. crypto.randomUUID
   is missing on older Safari (< 15.4) and outside secure contexts, and a
   throw here would take the Ask Andre button down with it. */
let requestCounter = 0;
function requestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  requestCounter += 1;
  return `andre-${Date.now().toString(36)}-${requestCounter}-${Math.random().toString(36).slice(2, 10)}`;
}

export function AndreWorkspace({ page, pageLabel, embedded, organisation, children }) {
  const [detail, setDetail] = useState(null);
  const [hostOrigin, setHostOrigin] = useState(null);
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(null);
  const context = useMemo(() => cleanAndreContext({ page, pageLabel, ...(detail?.page === page ? detail : {}) }), [page, pageLabel, detail]);
  const updateContext = useCallback(value => setDetail(value), []);

  useEffect(() => {
    if (!embedded) return undefined;
    const receive = event => {
      if (acceptsAndreReady(event, window.parent, location.origin, PREVIEW)) {
        setHostOrigin(event.origin);
        setOpen(false);
      }
    };
    window.addEventListener('message', receive);
    try {
      const origin = new URL(document.referrer).origin;
      if (allowedAndreHost(origin, location.origin, PREVIEW)) {
        window.parent.postMessage({ type: 'tacho:andre:hello', version: ANDRE_BRIDGE_VERSION }, origin);
      }
    } catch { /* An unrecognised host retains the local assistant. */ }
    return () => window.removeEventListener('message', receive);
  }, [embedded]);

  useEffect(() => {
    if (hostOrigin) window.parent.postMessage({ type: 'tacho:andre:context', version: ANDRE_BRIDGE_VERSION, context }, hostOrigin);
  }, [hostOrigin, context]);

  const ask = useCallback((prompt = '', selection = {}) => {
    const next = { prompt: String(prompt).slice(0, 2000), context: cleanAndreContext({ ...context, ...selection }), id: requestId() };
    if (hostOrigin) {
      window.parent.postMessage({ type: 'tacho:andre:open', version: ANDRE_BRIDGE_VERSION, ...next }, hostOrigin);
    } else {
      setRequest(next);
      setOpen(true);
    }
  }, [context, hostOrigin]);

  return (
    <AndreContext.Provider value={{ ask, updateContext, connected: Boolean(hostOrigin) }}>
      {children}
      {!hostOrigin && <ChatPanel open={open} onOpen={() => ask()} onClose={() => setOpen(false)} context={context} request={request} getReply={answerQuestion} sample={PREVIEW} organisation={organisation} launcher={false} />}
    </AndreContext.Provider>
  );
}

export function useAndre() { return useContext(AndreContext); }

export function useAndrePageContext(page, detail) {
  const update = useAndre()?.updateContext;
  const serialised = JSON.stringify(cleanAndreContext({ ...detail, page }));
  useEffect(() => {
    if (!update) return undefined;
    update(JSON.parse(serialised));
    return () => update(null);
  }, [update, serialised]);
}

export function AskAndreButton({ prompt = '', selection, children = 'Ask Andre', className = 'andre-page-action', ...props }) {
  const andre = useAndre();
  if (!andre) return null;
  return <button type="button" className={className} onClick={() => andre.ask(prompt, selection)} {...props}>{children}</button>;
}
